/**
 * Updates the numbering of tables and images in a report.
 *
 * This function takes a report object and updates the numbering of tables and images
 * within the report's chapters and sub-chapters. Each table is assigned a sequential
 * table number, and each image is assigned a sequential image number.
 *
 * @param {Report} report - The report object to update.
 * @returns {Report} - The updated report object with numbered tables and images.
 *
 * Usage:
 * const updatedReport = updateReportNumbering(report);
 */

import { Chapter, DataNode, NodeDataType, Report } from "graphql/types"

export const updateReportNumbering = (report: Report): Report => {
  let tableCounter = 1
  let imageCounter = 1

  const processChapter = (chapter: Chapter): Chapter => {
    const updatedData = chapter.data.map((node) => {
      if (node.dataType === NodeDataType.TABLE) {
        return { ...node, tableNumber: tableCounter++ }
      }
      if (node.dataType === NodeDataType.IMAGE) {
        const nodeImage: DataNode = node
        const newNumbers = nodeImage.image.map(() => imageCounter++)
        return { ...nodeImage, imageNumber: newNumbers }
      }
      return node
    })

    const updatedSubChapters = chapter.subChapter
      ? chapter.subChapter.map(processChapter)
      : chapter.subChapter

    return { ...chapter, data: updatedData, subChapter: updatedSubChapters }
  }

  const updatedChapters = report.chapter.map(processChapter)
  return { ...report, chapter: updatedChapters }
}
